import { memo, useMemo } from 'react';
import * as THREE from 'three';
import { Line } from '@react-three/drei';
import type { Entity } from '../state/store';
import { dispatchEntityClick, useEntityRegistryRef } from './toolEventBus';
import { useAnnotationInk } from '../hooks/useThemeInk';
import { useAppStore } from '../state/store';
import { isoGrid, sampleCurve, type NurbsSurfaceData } from '../core/geometry/nurbs';
import { BACKFACE_DEFINES } from './backfaceTint';

/**
 * Renderers for the NURBS kernel citizens (R1). The kernel data
 * (Entity.data.curve / .surface) is the source of truth; the display
 * geometry comes from the tessellation cache the runners regenerate on
 * every kernel mutation. Isocurves appear when the view store asks for
 * them (3D wireframe display of control structure).
 */

/** Display-mode contract (R6-2): wireframe/hiddenLine draw edges only;
 *  xray/ghosted fade; everything else shades. Mirrors SolidEntityMesh. */
function useDisplayMode() {
    const viewMode = useAppStore((s) => s.view.viewMode);
    const wireframe = viewMode === 'wireframe' || viewMode === 'hiddenLine';
    const opacity = (selected: boolean): number =>
        selected ? 1 : viewMode === 'xray' ? 0.25 : viewMode === 'ghosted' ? 0.35 : 0.92;
    return { wireframe, opacity };
}

export const NurbsCurveEntity = memo(function NurbsCurveEntity({
    entity, isSelected, isHovered,
}: { entity: Entity; isSelected: boolean; isHovered: boolean }) {
    const ink = useAnnotationInk();
    const color = isSelected ? '#00aaff' : isHovered ? '#ffaa00' : (entity.data.color as string) || ink;
    const registryRef = useEntityRegistryRef(entity.id, entity.layer);
    const points = useMemo(() => {
        const tess = entity.data.tessellation as number[][] | undefined;
        if (Array.isArray(tess) && tess.length >= 2) return tess.map((p) => [p[0], p[1], p[2] ?? 0] as [number, number, number]);
        const curve = entity.data.curve as { points?: number[][] } | undefined;
        return (curve?.points ?? []).map((p) => [p[0], p[1], p[2] ?? 0] as [number, number, number]);
    }, [entity.data]);
    if (points.length < 2) return null;
    return (
        <group ref={registryRef} onClick={(e) => { e.stopPropagation(); dispatchEntityClick(entity.id, e as any); }}>
            <Line points={points} color={color} lineWidth={isSelected ? 3 : 2} />
            {isSelected && (entity.data.curve as { points?: number[][] })?.points?.map((p, i) => (
                <mesh key={i} position={[p[0], p[1], p[2] ?? 0]}>
                    <sphereGeometry args={[0.045, 8, 8]} />
                    <meshBasicMaterial color="#ffff00" />
                </mesh>
            ))}
        </group>
    );
});

export const NurbsSurfaceEntity = memo(function NurbsSurfaceEntity({
    entity, isSelected, isHovered,
}: { entity: Entity; isSelected: boolean; isHovered: boolean }) {
    const color = isSelected ? '#00aaff' : isHovered ? '#ffaa00' : (entity.data.color as string) || '#66c2a5';
    const data = entity.data.surface as NurbsSurfaceData | undefined;
    const tess = entity.data.tessellation as { vertices: number[][]; indices: number[][] } | undefined;
    const { wireframe, opacity } = useDisplayMode();
    const registryRef = useEntityRegistryRef(entity.id, entity.layer);

    const geometry = useMemo(() => {
        if (!tess || !Array.isArray(tess.vertices) || tess.vertices.length === 0) return null;
        const geo = new THREE.BufferGeometry();
        const pos = new Float32Array(tess.vertices.length * 3);
        tess.vertices.forEach((v, i) => { pos[i * 3] = v[0]; pos[i * 3 + 1] = v[1]; pos[i * 3 + 2] = v[2] ?? 0; });
        geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
        const idx: number[] = [];
        (tess.indices ?? []).forEach((t) => { if (t.length === 3) idx.push(t[0], t[1], t[2]); });
        if (idx.length === 0) return null;
        geo.setIndex(idx);
        geo.computeVertexNormals();
        return geo;
    }, [tess]);

    const isoLines = useMemo(() => {
        if (!data) return [];
        // The kernel's own iso-grid: no duplicated evaluation here.
        return isoGrid(data, 5).map((curve) =>
            sampleCurve(curve, 16).map((p) => [p[0], p[1], p[2] ?? 0] as [number, number, number])
        );
    }, [data]);

    if (!geometry) return null;
    const handleClick = (e: { stopPropagation: () => void }) => {
        e.stopPropagation();
        dispatchEntityClick(entity.id, e as any);
    };
    // Wireframe display mode: edges of the tessellation cache, no fill.
    if (wireframe) {
        const edgePts: [number, number, number][] = [];
        const seen = new Set<string>();
        (tess?.indices ?? []).forEach(([a, b, c]) => {
            ([[a, b], [b, c], [c, a]] as [number, number][]).forEach(([x, y]) => {
                const k = x < y ? `${x}-${y}` : `${y}-${x}`;
                if (seen.has(k)) return;
                seen.add(k);
                const va = tess!.vertices[x], vb = tess!.vertices[y];
                edgePts.push([va[0], va[1], va[2] ?? 0], [vb[0], vb[1], vb[2] ?? 0]);
            });
        });
        return (
            <group>
                {edgePts.length >= 2 && (
                    <Line
                        ref={registryRef as any}
                        points={edgePts}
                        color={color}
                        lineWidth={isSelected ? 1.6 : 0.8}
                        onClick={handleClick}
                    />
                )}
                {isSelected && isoLines.map((pts, i) => (
                    <Line key={`iso${i}`} points={pts} color="#3ddc97" lineWidth={0.6} />
                ))}
            </group>
        );
    }
    return (
        <group>
            <mesh ref={registryRef} geometry={geometry} onClick={handleClick} castShadow receiveShadow>
                <meshStandardMaterial
                    color={color}
                    roughness={0.6}
                    metalness={0.1}
                    side={THREE.DoubleSide}
                    defines={BACKFACE_DEFINES}
                    transparent
                    opacity={opacity(isSelected)}
                />
            </mesh>
            {/* 3D-style isocurve display when the surface is selected */}
            {isSelected && isoLines.map((pts, i) => (
                <Line key={i} points={pts} color="#3ddc97" lineWidth={0.6} />
            ))}
        </group>
    );
});

/**
 * Polysurface: kernel face list with a merged tessellation cache. The
 * faces themselves are only re-tessellated by the command layer; here the
 * cache renders as a single watertight-looking mesh.
 */
export const NurbsPolysurfEntity = memo(function NurbsPolysurfEntity({
    entity, isSelected, isHovered,
}: { entity: Entity; isSelected: boolean; isHovered: boolean }) {
    const color = isSelected ? '#00aaff' : isHovered ? '#ffaa00' : (entity.data.color as string) || '#66c2a5';
    const tess = entity.data.tessellation as { vertices: number[][]; indices: number[][] } | undefined;
    const { opacity } = useDisplayMode();
    const registryRef = useEntityRegistryRef(entity.id, entity.layer);
    const geometry = useMemo(() => {
        if (!tess || !Array.isArray(tess.vertices) || tess.vertices.length === 0) return null;
        const geo = new THREE.BufferGeometry();
        const pos = new Float32Array(tess.vertices.length * 3);
        tess.vertices.forEach((v, i) => { pos[i * 3] = v[0]; pos[i * 3 + 1] = v[1]; pos[i * 3 + 2] = v[2] ?? 0; });
        geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
        const idx: number[] = [];
        (tess.indices ?? []).forEach((t) => { if (t.length === 3) idx.push(t[0], t[1], t[2]); });
        if (idx.length === 0) return null;
        geo.setIndex(idx);
        geo.computeVertexNormals();
        return geo;
    }, [tess]);
    if (!geometry) return null;
    return (
        <mesh
            ref={registryRef}
            geometry={geometry}
            onClick={(e) => { e.stopPropagation(); dispatchEntityClick(entity.id, e as any); }}
            castShadow
            receiveShadow
        >
            <meshStandardMaterial
                color={color}
                roughness={0.6}
                metalness={0.1}
                side={THREE.DoubleSide}
                defines={BACKFACE_DEFINES}
                transparent
                opacity={opacity(isSelected)}
            />
        </mesh>
    );
});
